import { useState, useEffect } from 'react';
import { useScrollAnimation, useStaggerAnimation } from '@/hooks/useScrollAnimation';

const SCENARIOS = [
  {
    id: 'identity',
    title: '无证件出行',
    icon: 'ri-fingerprint-line',
    color: '#00FFCC',
    emoji: '🪪',
    headline: '你的脸就是你的证件',
    story: '2035年，王芳去政府办事处办理房产过户。她走进大厅，门口的AI识别系统扫描了她的虹膜，0.3秒后屏幕显示：「王芳，欢迎！您的所有证件已验证完毕。」她没有带任何证件，甚至没有带手机。整个过户手续在15分钟内完成，所有文件自动生成并存入她的数字身份链。',
    features: ['虹膜识别', '掌纹验证', '声纹认证', '步态识别'],
    stats: [{ label: '识别精度', value: '99.9999%' }, { label: '验证时间', value: '0.3秒' }, { label: '支持场景', value: '10000+' }],
  },
  {
    id: 'payment',
    title: '无货币交易',
    icon: 'ri-hand-coin-line',
    color: '#FFD700',
    emoji: '💳',
    headline: '扫脸即付，无需货币',
    story: '2038年，张伟在超市购物，结账时他只是对着收银台的摄像头微微一笑。系统识别了他的面部特征，自动从他的数字资产账户中扣除了相应价值。没有现金，没有银行卡，没有手机支付——他的脸就是他的钱包。整个过程比刷卡还快，0.8秒完成。',
    features: ['人脸支付', '指纹支付', '掌纹支付', '声纹支付'],
    stats: [{ label: '支付速度', value: '0.8秒' }, { label: '全球覆盖', value: '195国' }, { label: '日交易量', value: '500亿次' }],
  },
  {
    id: 'assets',
    title: '万物数字化',
    icon: 'ri-building-line',
    color: '#FF6B9D',
    emoji: '🏠',
    headline: '所有资产都在链上',
    story: '2040年，李明把他在上海的公寓卖给了一个在火星的买家。整个交易过程：买家扫描掌纹确认身份→智能合约自动执行→房产所有权在区块链上转移→资金即时到账。全程无需律师、无需中介、无需银行，15分钟完成跨星球房产交易。',
    features: ['房产上链', '股权代币化', '艺术品NFT', '知识产权链'],
    stats: [{ label: '链上资产', value: '$500万亿', }, { label: '交易时间', value: '15分钟' }, { label: '手续费', value: '0.001%' }],
  },
  {
    id: 'medical',
    title: '医疗无纸化',
    icon: 'ri-heart-pulse-line',
    color: '#00FF88',
    emoji: '🏥',
    headline: '病历全球互通',
    story: '2036年，陈华在巴黎突发心脏病。救护车到达时，医生扫描了他的虹膜，立即获取了他在中国的完整病历、过敏史、用药记录。手术在最短时间内开始，因为医生已经了解了他的全部医疗信息。这次跨国急救，因为数字身份系统，挽救了他的生命。',
    features: ['全球病历', '基因档案', '用药记录', '手术历史'],
    stats: [{ label: '数据互通', value: '195国' }, { label: '获取时间', value: '即时' }, { label: '挽救生命', value: '每年200万' }],
  },
  {
    id: 'education',
    title: '学历全球认证',
    icon: 'ri-graduation-cap-line',
    color: '#A855F7',
    emoji: '🎓',
    headline: '学历证书永久上链',
    story: '2037年，刘洋在新加坡应聘。HR扫描了他的掌纹，立即看到了他在北京大学的学历证书、在MIT的在线课程证书、在火星大学的研究成果——所有证书都在区块链上，无法伪造，永久有效。整个背景调查在30秒内完成。',
    features: ['学历上链', '技能认证', '工作经历', '荣誉记录'],
    stats: [{ label: '认证机构', value: '50000+' }, { label: '验证时间', value: '30秒' }, { label: '防伪率', value: '100%' }],
  },
  {
    id: 'travel',
    title: '无护照出行',
    icon: 'ri-plane-line',
    color: '#FF6B35',
    emoji: '✈️',
    headline: '全球通行，无需护照',
    story: '2039年，赵敏从深圳飞往纽约。在机场，她走过一道扫描门，系统在0.5秒内完成了身份验证、签证确认、安全检查。没有护照，没有登机牌，没有行李托运单——一切都是数字化的。她甚至不需要停下脚步，直接走进登机口。',
    features: ['生物护照', '数字签证', '自动通关', '行李追踪'],
    stats: [{ label: '通关时间', value: '0.5秒' }, { label: '参与国家', value: '180国' }, { label: '年旅客量', value: '50亿人次' }],
  },
];

const TIMELINE = [
  { year: '2025', event: '数字身份试点', desc: '中国、欧盟开始数字身份证试点', color: '#00FF88' },
  { year: '2028', event: '生物支付普及', desc: '人脸支付覆盖全球主要城市', color: '#FFD700' },
  { year: '2031', event: '资产全面上链', desc: '房产、股权、艺术品全面数字化', color: '#FF6B9D' },
  { year: '2035', event: '无证件时代', desc: '全球首个无证件国家诞生', color: '#00FFCC' },
  { year: '2038', event: '无货币社会', desc: '生物特征成为唯一支付方式', color: '#A855F7' },
  { year: '2042', event: '全球数字公民', desc: '195国互认数字身份，无国界出行', color: '#FF6B35' },
];

export default function DigitalAssets() {
  const [activeScenario, setActiveScenario] = useState(0);
  const [animStep, setAnimStep] = useState(0);
  const [scanActive, setScanActive] = useState(false);
  const [scanResult, setScanResult] = useState('');

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ threshold: 0.08 });
  const { ref: demoRef, isVisible: demoVisible } = useScrollAnimation({ threshold: 0.06 });
  const { ref: timelineRef, visibleItems: timelineVisible } = useStaggerAnimation(TIMELINE.length, { threshold: 0.08 });
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation({ threshold: 0.08 });

  useEffect(() => {
    const interval = setInterval(() => setAnimStep((s) => (s + 1) % 4), 1200);
    return () => clearInterval(interval);
  }, []);

  const handleScan = () => {
    setScanActive(true);
    setScanResult('');
    setTimeout(() => {
      setScanResult('✓ 身份验证成功 · 欢迎，数字公民！');
      setTimeout(() => { setScanActive(false); setScanResult(''); }, 3000);
    }, 1500);
  };

  const scenario = SCENARIOS[activeScenario];

  return (
    <section id="digital-assets" className="relative py-24 md:py-32 overflow-hidden" style={{ background: 'linear-gradient(180deg, #050A10 0%, #070C14 50%, #050A10 100%)' }}>
      {/* Watermark */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/07ae511a5fdf3ec701a8d7d921f8d1fe.jpg"
          alt=""
          className="w-full h-full object-cover opacity-5"
        />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 60% 40%, rgba(0,255,204,0.06) 0%, transparent 60%)' }} />
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'linear-gradient(rgba(0,255,204,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,204,0.1) 1px, transparent 1px)',
          backgroundSize: '55px 55px',
        }} />
        {/* Mr. Doodle decorations */}
        <svg className="absolute top-0 left-0 w-full h-full opacity-12" viewBox="0 0 1440 900" preserveAspectRatio="none">
          <path d="M0,70 Q300,40 600,90 Q900,140 1200,70 Q1350,40 1440,80" stroke="#00FFCC" strokeWidth="2" fill="none" strokeDasharray="12,18" opacity="0.35"/>
          <circle cx="200" cy="200" r="80" stroke="#FFD700" strokeWidth="1.5" fill="none" strokeDasharray="8,12" opacity="0.2"/>
          <circle cx="1240" cy="700" r="100" stroke="#FF6B9D" strokeWidth="1.5" fill="none" strokeDasharray="8,12" opacity="0.2"/>
          <text x="60" y="700" fontSize="32" fill="#00FFCC" opacity="0.08" fontFamily="Caveat, cursive" transform="rotate(-25, 60, 700)">无证件时代</text>
          <text x="1050" y="150" fontSize="26" fill="#FFD700" opacity="0.08" fontFamily="Caveat, cursive" transform="rotate(12, 1050, 150)">2038</text>
        </svg>
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className="text-center mb-16"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
          }}
        >
          <div className="inline-flex items-center gap-2 px-5 py-2 mb-6 text-sm font-bold" style={{ background: 'rgba(0,255,204,0.12)', border: '1.5px solid rgba(0,255,204,0.45)', borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%', color: '#00FFCC', fontFamily: 'Caveat, cursive', fontSize: '1rem' }}>
            <i className="ri-fingerprint-line" />
            数字资产时代 · 2035-2042年 · 无证件·无货币·无边界
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00FFCC] via-[#FFD700] to-[#FF6B9D]">万物数字化</span>
            <span className="block text-2xl md:text-3xl mt-3 text-white/80">你的脸就是你的一切</span>
          </h2>
          <p className="text-white/50 text-base max-w-3xl mx-auto leading-relaxed">
            2035年，人类进入真正的数字公民时代。不需要证件、不需要货币、不需要扫码——
            你的生物特征就是你的身份、你的钱包、你的通行证。
          </p>
        </div>

        {/* Interactive Demo */}
        <div
          ref={demoRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16"
          style={{
            opacity: demoVisible ? 1 : 0,
            transform: demoVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.8s ease 0.15s, transform 0.8s ease 0.15s',
          }}
        >
          {/* Biometric Scanner Demo */}
          <div className="lg:col-span-1 flex flex-col items-center gap-6">
            <div
              className="relative w-64 h-64 flex items-center justify-center cursor-pointer"
              onClick={handleScan}
              style={{
                background: 'rgba(0,255,204,0.04)',
                border: `2px solid ${scanActive ? '#00FFCC' : 'rgba(0,255,204,0.25)'}`,
                borderRadius: '20px 14px 22px 16px / 14px 22px 16px 20px',
                boxShadow: scanActive ? '0 0 60px rgba(0,255,204,0.3)' : 'none',
                transition: 'all 0.3s ease',
              }}
            >
              {/* Scan animation */}
              {scanActive && (
                <div
                  className="absolute left-0 right-0 h-0.5 pointer-events-none"
                  style={{
                    background: 'linear-gradient(90deg, transparent, #00FFCC, transparent)',
                    animation: 'scanLine 1.5s ease-in-out',
                    top: '50%',
                  }}
                />
              )}
              <div className="flex flex-col items-center gap-4">
                <div
                  className="w-24 h-24 flex items-center justify-center"
                  style={{
                    background: scanActive ? 'rgba(0,255,204,0.2)' : 'rgba(0,255,204,0.08)',
                    border: `2px solid ${scanActive ? '#00FFCC' : 'rgba(0,255,204,0.3)'}`,
                    borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <i className="ri-fingerprint-line text-5xl" style={{ color: scanActive ? '#00FFCC' : 'rgba(0,255,204,0.5)' }} />
                </div>
                <p className="text-white/60 text-sm text-center" style={{ fontFamily: 'Caveat, cursive', fontSize: '1rem' }}>
                  {scanActive ? '扫描中...' : '点击体验生物识别'}
                </p>
                {scanResult && (
                  <p className="text-[#00FFCC] text-sm font-bold text-center" style={{ fontFamily: 'Caveat, cursive', fontSize: '0.95rem' }}>{scanResult}</p>
                )}
              </div>
              {/* Corner decorations */}
              {['top-2 left-2', 'top-2 right-2', 'bottom-2 left-2', 'bottom-2 right-2'].map((pos, i) => (
                <div key={i} className={`absolute ${pos} w-4 h-4`} style={{ border: `2px solid rgba(0,255,204,${scanActive ? 0.8 : 0.3})`, borderRadius: i < 2 ? '4px 0 0 0' : '0 0 4px 0', transition: 'all 0.3s ease' }} />
              ))}
            </div>

            {/* Biometric types */}
            <div className="grid grid-cols-2 gap-3 w-full">
              {[
                { icon: 'ri-eye-line', label: '虹膜识别', color: '#00FFCC' },
                { icon: 'ri-fingerprint-line', label: '指纹识别', color: '#FFD700' },
                { icon: 'ri-user-smile-line', label: '人脸识别', color: '#FF6B9D' },
                { icon: 'ri-hand-heart-line', label: '掌纹识别', color: '#A855F7' },
              ].map((bio) => (
                <div key={bio.label} className="flex items-center gap-2 p-2.5 rounded-xl" style={{ background: `${bio.color}08`, border: `1px solid ${bio.color}25`, borderRadius: '10px 8px 12px 8px / 8px 12px 8px 10px' }}>
                  <i className={`${bio.icon} text-sm`} style={{ color: bio.color }} />
                  <span className="text-white/60 text-xs">{bio.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Scenario Cards */}
          <div className="lg:col-span-2">
            {/* Scenario selector */}
            <div className="flex flex-wrap gap-2 mb-5">
              {SCENARIOS.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => setActiveScenario(i)}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold transition-all duration-300 cursor-pointer whitespace-nowrap"
                  style={{
                    background: activeScenario === i ? `${s.color}15` : 'rgba(255,255,255,0.03)',
                    border: `1.5px solid ${activeScenario === i ? s.color + '60' : 'rgba(255,255,255,0.1)'}`,
                    borderRadius: '8px 6px 10px 8px / 6px 10px 8px 6px',
                    color: activeScenario === i ? s.color : 'rgba(255,255,255,0.4)',
                    transform: activeScenario === i ? 'scale(1.05)' : 'scale(1)',
                    fontFamily: 'Caveat, cursive',
                    fontSize: '0.88rem',
                  }}
                >
                  <span>{s.emoji}</span>
                  {s.title}
                </button>
              ))}
            </div>

            {/* Active scenario */}
            <div
              className="rounded-2xl p-6 transition-all duration-500"
              style={{
                background: `${scenario.color}06`,
                border: `2px solid ${scenario.color}30`,
                borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px',
              }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 flex items-center justify-center text-2xl" style={{ background: `${scenario.color}15`, border: `1.5px solid ${scenario.color}40`, borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%' }}>
                  {scenario.emoji}
                </div>
                <div>
                  <h3 className="text-white font-black text-xl" style={{ fontFamily: 'Orbitron, sans-serif', fontSize: '1.1rem' }}>{scenario.title}</h3>
                  <p className="font-bold" style={{ color: scenario.color, fontFamily: 'Caveat, cursive', fontSize: '1rem' }}>{scenario.headline}</p>
                </div>
              </div>

              <p className="text-white/70 text-sm leading-relaxed mb-5" style={{ fontFamily: 'Caveat, cursive', fontSize: '0.95rem' }}>{scenario.story}</p>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-white/40 text-xs mb-2 font-bold tracking-wider">核心功能</p>
                  <div className="flex flex-wrap gap-1.5">
                    {scenario.features.map((f) => (
                      <span key={f} className="px-2.5 py-1 text-xs" style={{ background: `${scenario.color}12`, border: `1px solid ${scenario.color}30`, borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%', color: scenario.color, fontFamily: 'Caveat, cursive', fontSize: '0.8rem' }}>
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-white/40 text-xs mb-2 font-bold tracking-wider">关键数据</p>
                  <div className="flex flex-col gap-1.5">
                    {scenario.stats.map((stat) => (
                      <div key={stat.label} className="flex items-center justify-between">
                        <span className="text-white/40 text-xs">{stat.label}</span>
                        <span className="font-bold text-xs" style={{ color: scenario.color, fontFamily: 'Orbitron, sans-serif' }}>{stat.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="mb-16">
          <h3 className="text-white font-bold text-xl mb-8 text-center" style={{ fontFamily: 'Caveat, cursive', fontSize: '1.5rem' }}>
            <span style={{ color: '#00FFCC' }}>数字化进程</span>时间线 · 2025-2042
          </h3>
          <div className="relative">
            <div className="absolute top-6 left-0 right-0 h-0.5" style={{ background: 'linear-gradient(90deg, #00FF88, #FFD700, #FF6B9D, #00FFCC, #A855F7, #FF6B35)' }} />
            <div
              ref={timelineRef as React.RefObject<HTMLDivElement>}
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4"
            >
              {TIMELINE.map((item, i) => (
                <div
                  key={item.year}
                  className="flex flex-col items-center gap-3 pt-2"
                  style={{
                    opacity: timelineVisible[i] ? 1 : 0,
                    transform: timelineVisible[i] ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.97)',
                    transition: `opacity 0.6s ease ${i * 100}ms, transform 0.6s ease ${i * 100}ms`,
                  }}
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full z-10" style={{ background: `${item.color}20`, border: `2px solid ${item.color}`, boxShadow: `0 0 16px ${item.color}40` }}>
                    <span className="font-black text-xs" style={{ color: item.color, fontFamily: 'Orbitron, sans-serif' }}>{item.year.slice(2)}</span>
                  </div>
                  <div className="text-center">
                    <p className="text-white font-bold text-xs mb-1" style={{ color: item.color }}>{item.event}</p>
                    <p className="text-white/40 text-xs leading-tight">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Vision statement */}
        <div
          ref={ctaRef as React.RefObject<HTMLDivElement>}
          className="rounded-3xl p-10 text-center relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(0,255,204,0.06), rgba(255,215,0,0.06))',
            border: '2px solid rgba(0,255,204,0.2)',
            borderRadius: '24px 18px 28px 20px / 18px 28px 20px 24px',
            opacity: ctaVisible ? 1 : 0,
            transform: ctaVisible ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.97)',
            transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s',
          }}
        >
          <div className="absolute top-4 left-8 text-3xl opacity-15" style={{ color: '#00FFCC', fontFamily: 'Caveat, cursive', transform: 'rotate(-12deg)' }}>✦</div>
          <div className="absolute bottom-4 right-12 text-2xl opacity-15" style={{ color: '#FFD700', fontFamily: 'Caveat, cursive', transform: 'rotate(8deg)' }}>★</div>
          <h3 className="text-white font-black text-3xl md:text-4xl mb-4" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00FFCC] to-[#FFD700]">热血沸腾</span>的未来
          </h3>
          <p className="text-white/60 text-base max-w-3xl mx-auto leading-relaxed">
            想象一下：你走遍全球，不需要护照；你买遍天下，不需要钱包；你办理一切，不需要证件。
            你的生物特征就是你的一切——这不是科幻，这是2035年的现实。
            <strong className="text-[#00FFCC]">元宇宙与区块链技术</strong>正在让这一切成为可能。
          </p>
        </div>
      </div>

      <style>{`
        @keyframes scanLine {
          0% { top: 10%; opacity: 0; }
          20% { opacity: 1; }
          80% { opacity: 1; }
          100% { top: 90%; opacity: 0; }
        }
      `}</style>
    </section>
  );
}
